import { assert_false, assert_not_null, assert_true, assert_type_string, not_implemented } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { FN_SERIALIZATION__data_serialize } from '@shared/serialization/FN_SERIALIZATION__data_serialize';
import { MAP__field_data } from '@shared/types/types';
import { UTILS__type } from '@shared/utils/UTILS__type';
import { UTILS__array } from '@shared/utils/utils_array';
import { logw } from '@shared/utils/utils_logs';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { FIELD__base } from './FIELD__base';

export interface PROPS__serialization {
  omit_class_name?: boolean;
  serialization_for_hashing?: boolean;
}

const PROPS__serialization_default: PROPS__serialization = {
  omit_class_name: false,
  serialization_for_hashing: false,
};

export const PROPS__serialization_hashing: PROPS__serialization = {
  omit_class_name: true,
  serialization_for_hashing: true,
};

declare module './FIELD__base' {
  interface FIELD__base {
    //key - field role
    store__serialize(graph: DTO_GRAPH__instance, props?: PROPS__serialization): MAP__field_data;

    store__serialize_field(graph: DTO_GRAPH__instance, props: PROPS__serialization, container: Record<string, any>, field_name: string, value: any): void;

    /*
     *
     */

    store__deserialize(data: MAP__field_data): void;

    store__deserialize_field_value(field_name: string, value_from_this: any, value: any): any;

    //return true if deserialized
    store__can_deserialize_from_data(data: MAP__field_data): boolean;

    /*
     *
     */
    store__serialization_keys_list_black(): string[];

    store__serialization_keys_list_white_for_hash(): string[];

    populate_from<T extends FIELD__base>(graph: DTO_GRAPH__instance, other: T): void;
  }
}

export function INIT_EXTENSION_FIELD_BASE__share() {
  // do nothing
}

/*
 * CREATE
 */

FIELD__base.prototype.store__serialize = function (graph: DTO_GRAPH__instance, props: PROPS__serialization = {}) {
  assert_false(this.meta.data__is_linked);
  if (this.meta.kind__is_output) return MAP__serializable.CREATE_STUB() as MAP__field_data;

  /*
   *
   */

  props = { ...PROPS__serialization_default, ...props };

  let { omit_class_name, serialization_for_hashing } = props;

  assert_not_null(omit_class_name);
  assert_not_null(serialization_for_hashing);

  let out: MAP__field_data = new MAP__serializable();

  let entries = Object.entries(this);

  if (serialization_for_hashing) {
    //work with white lists
    let keys_list__white: string[] = this.store__serialization_keys_list_white_for_hash();

    for (let [key, value] of entries) {
      if (!keys_list__white.includes(key)) continue;

      this.store__serialize_field(graph, props, out, key, value);
    }
  } else {
    //work with black lists
    let keys_list__black: string[] = this.store__serialization_keys_list_black();

    for (let [key, value] of entries) {
      if (keys_list__black.includes(key)) continue;

      this.store__serialize_field(graph, props, out, key, value);
    }
  }

  if (!out.is_empty() && !omit_class_name) {
    out.set(CONSTANTS__shared.SHARE_FIELD_CLASS_NAME, this._class_name_instance);
  }

  if (serialization_for_hashing && !this.get_is_tested_for_hash()) debugger;
  if (!serialization_for_hashing && !this.get_is_tested()) debugger;

  return out;
};

FIELD__base.prototype.store__serialize_field = function (
  graph: DTO_GRAPH__instance,
  //
  props: PROPS__serialization,
  container: MAP__field_data,
  //
  field_name: string,
  field_value: any,
): void {
  assert_false(this.meta.data__is_linked);
  assert_true(this.meta.kind__is_input);

  let data__value;

  if (field_value == null) {
    logw(`${field_name} is null`);
  } else if (field_value instanceof FIELD__base) {
    field_value = field_value.find_source(graph);

    data__value = field_value.store__serialize(graph, props);

    if (!data__value.is_empty()) container.set(field_name, data__value);
  } else {
    data__value = FN_SERIALIZATION__data_serialize(field_value);

    container.set(field_name, data__value);
  }
};

/*
 *
 */

// todo:  deserialization for any field
//     check existance inside field, use field name
//     check type inside field and inside data
//
//.    additional checkls for image asset, image asset list

FIELD__base.prototype.store__deserialize = function (map: MAP__serializable<string, any>) {
  assert_false(this.meta.data__is_linked);
  assert_true(this.meta.kind__is_input);

  if (!this.store__can_deserialize_from_data(map)) return;

  let entries = map.entries();

  for (let [key, value] of entries) {
    if (!(key in this)) continue;

    let value_from_this = (this as any)[key];

    if (value_from_this == null) continue; //can't get info from null

    let value_deserialized = this.store__deserialize_field_value(key, value_from_this, value);

    if (value_deserialized === undefined || value_deserialized === null) continue;

    (this as any)[key] = value_deserialized;
  }
};

FIELD__base.prototype.store__deserialize_field_value = function (field_name: string, value_from_this: any, value: any): any {
  assert_false(this.meta.data__is_linked);
  assert_true(this.meta.kind__is_input);

  let value_map: MAP__field_data = UTILS__type.is_object(value) ? MAP__serializable.from_object(value) : new MAP__serializable();

  let out: any = undefined;

  if (Array.isArray(value_from_this) && Array.isArray(value) && UTILS__array.are_both_empty(value_from_this, value)) {
    // do nothing
  } else if (UTILS__type.is_primitive(value)) {
    if (!UTILS__type.are_same_primitive_types(value, value_from_this)) return;

    out = value;
  } else if (UTILS__type.is_array_of__primitive(value)) {
    if (!UTILS__type.are_same_type_array_of_primitives(value, value_from_this)) return;

    out = [...value];
  } else if (value_from_this instanceof FIELD__base) {
    if (value_from_this.store__can_deserialize_from_data(value_map)) {
      value_from_this.store__deserialize(value_map);
    } else {
      // todo:test why cant deserialize
      debugger;
      value_from_this.store__can_deserialize_from_data(value_map);
    }
  } else if (Array.isArray(value_from_this)) {
    //implement this case
    debugger;
  } else if (value_from_this instanceof MAP__serializable) {
    let value_map = MAP__serializable.from_object(value);
    value_from_this.merge_with(value_map);
  } else if (value_from_this instanceof DTO__base) {
    assert_false(value_from_this instanceof FIELD__base);
    assert_true(UTILS__type.is_object(value));

    out = value_from_this.clone(value);
  } else {
    //check this case
    debugger;
  }

  return out;
};

FIELD__base.prototype.store__can_deserialize_from_data = function (map: MAP__serializable<string, any>) {
  assert_false(this.meta.data__is_linked);
  assert_true(this.meta.kind__is_input);

  if (!map.has(CONSTANTS__shared.SHARE_FIELD_CLASS_NAME)) return false;

  let class_name_instance = map.get_or_default(CONSTANTS__shared.SHARE_FIELD_CLASS_NAME, '');
  assert_type_string(class_name_instance);

  return class_name_instance == this._class_name_instance;
};

FIELD__base.prototype.store__serialization_keys_list_black = function <T extends FIELD__base>(): (keyof T)[] {
  return ['meta', '_is_stub', CONSTANTS__shared.SERIALIZATION_CLASS_FIELD_INSTANCE];
};

FIELD__base.prototype.store__serialization_keys_list_white_for_hash = function <T extends FIELD__base>(): (keyof T)[] {
  not_implemented();
};

FIELD__base.prototype.populate_from = function <T extends FIELD__base>(graph: DTO_GRAPH__instance, other: T): void {
  assert_true(this.meta.kind__is_input);
  assert_false(this.meta.data__is_linked);

  let store__data = other.store__serialize(graph);

  if (store__data.is_stub()) return;
  if (store__data.is_empty()) return;

  let can_deserialize = this.store__can_deserialize_from_data(store__data);
  assert_true(can_deserialize);

  this.store__deserialize(store__data);

  let fields_map = this.fields__get_map(true);

  for (let [field_name, field] of fields_map.entries()) {
    field.meta.id_create();
    field.copy_meta_from_parent(field);
  }
};
